import './root.css';

import { useState, useEffect, useMemo, useRef, Suspense, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../components/sidebar/sidebar';
import Topbar from '../components/topbar/topbar';
import useScrollStore from '../stores/scrollStore';
import { throttle } from 'lodash';
import useAlertStore from '../stores/alertStore';
import useInvestorDetailStore from '../stores/investorDetailStore';
import useViewportHeight from '../hooks/useViewportHeight';
import { checkSession, logout } from '../services/authService';
import useConstructionUpdateStore from '../stores/constructionUpdateStore';
import ScrollUpButton from '../components/scroll-up-button/scroll-up-button';
import useUnitDetailStore from '../stores/unitDetailStore';
import useDashboardStore, { DASHBOARD_STORAGE_NAME } from '../stores/dashboardStore';
import useUnitStore, { UNIT_STORAGE_NAME } from '../stores/unitStore';
import NavigationBottomBar from '../components/bottom-navigation-bar/bottom-navigation-bar';
import { usePWA } from '../context/PWAContext';
import useIsMobile from '../hooks/useIsMobile';
import Alert from '../components/alert/alert';
import { ContentAreaProvider } from '../context/ContentAreaContext';
import { colorPalette } from '../utils/theme';
import ContactUsModal from '../modals/contact-us-modal/contact-us-modal';
import * as Sentry from "@sentry/react";
import { Capacitor } from '@capacitor/core';
import { Style } from '@capacitor/status-bar';
import { StatusBar } from '@capacitor/status-bar';
import useTenantDetailStore from '../stores/tenantDetailStore';
import AppUrlListener from './app-url-listener';

const Root = () => {
    useViewportHeight();

    const location = useLocation();
    const isPWA = usePWA();
    const isMobile = useIsMobile();

    // References
    const contentAreaRef = useRef<HTMLDivElement>(null);

    // Stores
    const { investor, reset: resetInvestoreDetailStore, setInvestor } = useInvestorDetailStore();
    const { tenant, reset: resetTenantDetailStore, setTenant } = useTenantDetailStore();
    const setAtBottom = useScrollStore(state => state.setAtBottom);
    const hideAlert = useAlertStore(state => state.hideAlert);
    const resetUnitStore = useUnitStore(state => state.reset);
    const resetUnitDetailStore = useUnitDetailStore(state => state.reset);
    const resetConstructionUpdateStore = useConstructionUpdateStore(state => state.reset);
    const resetDashboardStore = useDashboardStore(state => state.reset);

    // States
    const [lastVisible, setLastVisible] = useState<number>(Date.now());
    const [isSidebarVisible, setSidebarVisible] = useState(false);

    // Functions
    const scrollToTop = useCallback(() => {
        const contentArea = contentAreaRef?.current;
        if (!contentArea) return;

        // Verwendung eines kleinen Timeouts, um sicherzustellen, dass der Scroll nach Seitenwechseln funktioniert
        setTimeout(() => {
            if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
                // For iOS native, use direct jump
                contentArea.scrollTop = 0;

                // Zusätzliche Sicherheit: Nach 50ms nochmal scrollen, falls der erste Versuch fehlschlägt
                setTimeout(() => {
                    if (contentArea.scrollTop > 0) {
                        contentArea.scrollTop = 0;
                    }
                }, 50);
            } else {
                // For other platforms, use smooth scrolling
                contentArea.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }, 50);
    }, [contentAreaRef]);

    const runCheckAuth = async () => {
        try {
            await checkSession();
        } catch (error) {
            console.log('Error while checking user session: ', error);
            await logout();

            resetUnitStore();
            resetConstructionUpdateStore();
            resetInvestoreDetailStore();
            resetTenantDetailStore();
            resetUnitDetailStore();
            resetDashboardStore();
        }
    };

    // Initial authentication check on app startup
    useEffect(() => {
        const checkInitialAuth = async () => {
            const { setIsLoading: setInvestorLoading, setIsInitalLoaded: setInvestorLoaded } = useInvestorDetailStore.getState();
            const { setIsLoading: setTenantLoading, setIsInitalLoaded: setTenantLoaded } = useTenantDetailStore.getState();
            
            try {
                // Check if user is authenticated
                await checkSession();
                
                // If we reach here, user is authenticated - try to fetch user data
                // First check if we already have user data in stores (from persistence)
                if (!investor && !tenant) {
                    try {
                        // Try to fetch investor data first
                        const { fetchInvestor } = await import('../services/investor/investorService');
                        const investorData = await fetchInvestor();
                        setInvestor(investorData);
                        setInvestorLoaded(true);
                    } catch (error) {
                        // If investor fetch fails, try tenant
                        try {
                            const { fetchTenant } = await import('../services/tenant/tenantService');
                            const tenantData = await fetchTenant();
                            setTenant(tenantData);
                            setTenantLoaded(true);
                        } catch (tenantError) {
                            console.log('User is authenticated but no valid user data found');
                        }
                    }
                } else {
                    // If we already have user data from persistence, mark as loaded
                    if (investor) {
                        setInvestorLoaded(true);
                    }
                    if (tenant) {
                        setTenantLoaded(true);
                    }
                }
            } catch (error) {
                // User is not authenticated - that's fine
                console.log('User not authenticated on startup');
            } finally {
                // Always set loading to false when we're done
                setInvestorLoading(false);
                setTenantLoading(false);
            }
        };

        checkInitialAuth();
    }, []); // Run only once on component mount

    // Set sentry user
    useEffect(() => {
        if (!investor && !tenant) {
            Sentry.setUser(null);
            return;
        }

        if (investor) {
            Sentry.setUser({
                type: "investor",
                id: investor.investor_id,
                email: investor.email,
                username: `${investor.first_name} ${investor.last_name}`
            });
        } else if (tenant) {
            Sentry.setUser({
                type: "tenant",
                id: tenant.tenant_id,
                email: tenant.email,
                username: `${tenant.first_name} ${tenant.last_name}`
            });
        }
    }, [investor, tenant]);

    // Reset scrolling state
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const categoryId = searchParams.get('categoryId');

        if (location.pathname !== "/faq" || (location.pathname === "/faq" && !categoryId)) {
            // Priorität: Scrolling sofort starten
            scrollToTop();

            // Zusätzliche Sicherheit: Nach 100ms nochmal scrollen, falls der erste Versuch fehlschlägt
            const timer = setTimeout(() => {
                const contentArea = contentAreaRef?.current;
                if (contentArea && contentArea.scrollTop > 0) {
                    scrollToTop();
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [location, scrollToTop, contentAreaRef]);

    // Clear old unused local storage
    useEffect(() => {
        localStorage.removeItem(DASHBOARD_STORAGE_NAME);
        localStorage.removeItem(UNIT_STORAGE_NAME);
    }, []);

    // Scroll handler to track reach bottom
    useEffect(() => {
        const scrollContainer = contentAreaRef.current;
        if (scrollContainer) {
            const handleScroll = throttle(() => {
                const scrollPosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
                const offsetHeight = scrollContainer.scrollHeight;
                const reachedBottom = scrollPosition >= offsetHeight - 80;
                setAtBottom(reachedBottom);
            }, 300);

            scrollContainer.addEventListener('scroll', handleScroll);

            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [setAtBottom, contentAreaRef]);

    // Hide alert after navigation
    useEffect(() => {
        hideAlert();
        if (location.pathname === "/home" || location.pathname === "/login" || location.pathname === "/forgot-password") {
            setSidebarVisible(false);
        }
    }, [location]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                setLastVisible(Date.now());
            } else if (document.visibilityState === 'visible') {
                const now = Date.now();
                const diff = now - lastVisible;

                if (diff > 1800000) { // 30 min
                    runCheckAuth();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [lastVisible]);

    useEffect(() => {
        if (investor || tenant) {
            runCheckAuth();
        }
    }, [investor, tenant]);

    /*useEffect(() => {
        if (contentAreaRef.current) {
            const isMobileView = () => window.innerWidth < 992;

            if (isMobileView()) {
                contentAreaRef.current.style.overflowY = isSidebarVisible ? 'hidden' : 'auto';
            }

            return () => {
                if (isMobileView()) {
                    document.body.style.overflow = 'auto';
                }
            };
        }
    }, [isSidebarVisible]);*/

    const toggleSidebar = () => {
        setSidebarVisible(!isSidebarVisible);
    };

    const isFullPage = useMemo(() => {
        return location.pathname === "/" || location.pathname === "/download" || location.pathname === "/home" || location.pathname === "/details" ||
            (location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) || location.pathname === "/login" || location.pathname === "/reset-password" ||
            location.pathname === "/packages" || location.pathname === "/privacy-policy" ||
            location.pathname === "/terms-and-conditions" || location.pathname === "/onboarding" || location.pathname === "/handover" || location.pathname === "/tenant-registration"
    }, [location]);

    const theme = useMemo(() => {
        return location.pathname === "/" || location.pathname === "/dashboard" || location.pathname.includes("/home") || location.pathname.includes("/details") || (location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) ? "dark" : "light";
    }, [location]);

    // Effects
    useEffect(() => {
        const backgroundColor = theme === "dark" ? colorPalette.primaryDark : "white";
        document.body.style.backgroundColor = backgroundColor;
        document.documentElement.style.backgroundColor = backgroundColor;

        // Only run StatusBar changes on native platforms
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
            StatusBar.setStyle({ style: theme === "dark" ? Style.Dark : Style.Light }).catch(() => { });
            StatusBar.setBackgroundColor({ color: backgroundColor }).catch(() => { });
        }
    }, [theme]);

    return (
        <ContentAreaProvider contentAreaRef={contentAreaRef}>
            <AppUrlListener />
            <Suspense fallback={<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>}>
                <div>
                    <Alert />
                    {isFullPage ? (
                        <>
                            {location.pathname !== "/download" && location.pathname !== "/home" && location.pathname !== "/details" && !(location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) && location.pathname !== "/login" && location.pathname !== "/handover" && location.pathname !== "/tenant-registration" && (
                                <Topbar toggleSidebar={toggleSidebar} theme={theme} isFullPage deactivateSidebar />
                            )}
                            <div id={location.pathname === "/download" || location.pathname === "/home" || location.pathname === "/details" || (location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) || location.pathname === "/login" ? "content-area-full-page" : "content-area"} ref={contentAreaRef}>
                                <Outlet />
                            </div>
                        </>
                    ) : (
                        <>
                            <Sidebar isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
                            <div id="detail">
                                <Topbar toggleSidebar={toggleSidebar} theme={theme} />
                                <div id="content-area" ref={contentAreaRef} style={((isMobile && isPWA) || !isMobile) ? { paddingBottom: 70 } : undefined}>
                                    <Outlet />
                                    <ScrollUpButton scrollableRef={contentAreaRef} />
                                </div>
                            </div>
                            <NavigationBottomBar />
                        </>
                    )}
                </div>
            </Suspense>

            <ContactUsModal />
        </ContentAreaProvider>
    );
};

export default Root;
